import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Textarea } from '@heyroot/ui/primitives/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import { ChevronLeft } from 'lucide-react';
import { useAdminAuth } from '../lib/auth-context';
import { PERMS } from '../../rbac/permissions';
import {
  isIllegalTransition,
  isRetryable,
  isEraseStorageIncomplete,
  isVersionConflict,
  forgetErasedSubmission,
  useEraseFeedbackSubmission,
  useFeedbackAttachments,
  useFeedbackSubmission,
  useUpdateFeedbackSubmission,
} from '../lib/use-feedback';
import { fetchAttachmentSignedUrl, isApiError, type AdminFeedbackDto } from '../lib/api';
import { TypedConfirm } from '../components/typed-confirm';
import {
  ATTACHMENT_RETENTION_DAYS,
  ERASE_NOT_FOUND_TEXT,
  ERASE_STORAGE_INCOMPLETE_TEXT,
  ERASE_UNAVAILABLE_TEXT,
  REQUEST_REF_HELP,
  REQUEST_REF_PATTERN,
  isAttachmentExpired,
} from '../lib/feedback-retention';
import {
  isTerminalStatus,
  legalNextStatuses,
  priorityVariant,
  requestTypeVariant,
  statusBadgeVariant,
  statusLabel,
  type FeedbackStatus,
} from '../lib/feedback-fsm';

interface Props {
  id: string;
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-caption text-muted">{label}</p>
      <div className="text-body-sm text-primary">
        {value ?? <span className="text-muted">—</span>}
      </div>
    </div>
  );
}

// What the form was LOADED from: values plus the rowVersion they belong to. Save compares
// against, and sends the version of, THIS -- never the live query data -- so an edit made
// against an older row can only ever be submitted under that older version (and 409), no
// matter what a background/failed refetch did to the cache underneath the form.
interface Baseline {
  status: string;
  notes: string;
  rowVersion: number;
}
function toBaseline(row: {
  status: string;
  adminNotes: string | null;
  rowVersion: number;
}): Baseline {
  return { status: row.status, notes: row.adminNotes ?? '', rowVersion: row.rowVersion };
}

const asText = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/**
 * The context the widget captured, as something an operator can act on: the page it was filed
 * from, the browser and the deployed build (to reproduce), and the viewport. Everything is
 * optional (older submissions predate userAgent/appVersion) and rendered as plain text.
 */
function ContextSummary({ context }: { context: Record<string, unknown> }) {
  const page = asText(context['currentPage']);
  const detectedModule = asText(context['detectedModule']);
  const userAgent = asText(context['userAgent']);
  const appVersion = asText(context['appVersion']);
  const vp = context['viewport'] as { w?: unknown; h?: unknown } | undefined;
  const viewport =
    typeof vp?.w === 'number' && typeof vp?.h === 'number' ? `${vp.w} × ${vp.h}` : null;
  if (!page && !detectedModule && !userAgent && !appVersion && !viewport) return null;
  return (
    <section
      aria-label="Where it happened"
      className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-control border border-border bg-surface p-4"
    >
      <Field
        label="Page"
        value={page ? <code className="break-all text-caption">{page}</code> : null}
      />
      <Field label="Detected module" value={detectedModule} />
      <Field label="Viewport" value={viewport} />
      <Field
        label="Build"
        value={appVersion ? <code className="text-caption">{appVersion}</code> : null}
      />
      <div className="sm:col-span-2">
        <Field label="Browser" value={userAgent} />
      </div>
    </section>
  );
}

interface ConflictDraft {
  status: string | null;
  notes: string | null;
  publicNote?: string | null;
}

type Banner =
  // `draft` is the operator's own edit that lost the race, kept readable so they can
  // consciously re-apply it against the reloaded values.
  | { kind: 'conflict'; draft?: ConflictDraft }
  | { kind: 'illegal'; from: string | undefined; to: string | undefined }
  | { kind: 'retryable' }
  | { kind: 'error'; message: string };

export function FeedbackDetailPage({ id }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPerm } = useAdminAuth();
  const canResolve = hasPerm(PERMS.FEEDBACK_RESOLVE);
  // Erasing is irreversible and is its own permission: triage access does not include it.
  const canErase = hasPerm(PERMS.FEEDBACK_ERASE);

  const { data, isPending, isError, refetch } = useFeedbackSubmission(id);
  const mutation = useUpdateFeedbackSubmission(id);
  const attachments = useFeedbackAttachments(id);
  const eraseMutation = useEraseFeedbackSubmission(id);
  // The erase confirmation is inline (TypedConfirm). `eraseError` is shown inside it so the operator can
  // correct the reference or retry without losing their place.
  const [eraseOpen, setEraseOpen] = useState(false);
  const [eraseError, setEraseError] = useState<string | null>(null);
  const [openingAttachmentId, setOpeningAttachmentId] = useState<string | null>(null);
  const [status, setStatus] = useState<string>(() => data?.status ?? '');
  const [notes, setNotes] = useState<string>(() => data?.adminNotes ?? '');
  // Staff-authored note for the submitter; only offered (and only sent) when moving a request into
  // Completed or Declined.
  const [publicNote, setPublicNote] = useState('');
  const [banner, setBanner] = useState<Banner | null>(null);
  const [baseline, setBaseline] = useState<Baseline | null>(() => (data ? toBaseline(data) : null));

  // The clickable chip only renders for avScanStatus === 'clean' inside the
  // 90-day retention window (isAttachmentExpired), so the upstream's scan
  // gating (pending → 409, infected → 410, scan_failed → 422) can't actually
  // reach this path. ONE expected failure still can: 410 ATTACHMENT_EXPIRED
  // (#14561: the bucket lifecycle rule has deleted the bytes) when the page was
  // loaded just before the boundary or the browser clock lags the server; its
  // `message` lands in the banner below via the thrown error.
  // Anything else here is a genuine problem (network blip, expired admin
  // session, root-api/GCS down). `window.open` is called BEFORE the await so
  // the tab is still tied to the click's user-activation window; popup
  // blockers (Safari always, Chromium after ~5s of async work) would
  // otherwise silently eat the open once `await` breaks that tie.
  //
  // Deliberately NOT passing the 'noopener'/'noreferrer' feature string here:
  // per the WHATWG spec, window.open() returns null WHENEVER noopener is
  // requested, regardless of whether a popup was actually blocked — an
  // earlier version of this code passed it and the `if (!popup)` check below
  // was therefore always true, permanently showing the "blocked" banner and
  // never navigating the tab, on every browser, every time. Severing the
  // opener relationship (the actual security property noopener provides) is
  // done manually via `popup.opener = null` instead, which preserves the
  // real window reference so the blocked-vs-not check is meaningful.
  async function openAttachment(attachmentId: string) {
    const popup = window.open('', '_blank');
    if (popup) popup.opener = null;
    setOpeningAttachmentId(attachmentId);
    try {
      const url = await fetchAttachmentSignedUrl(id, attachmentId);
      if (!popup) {
        setBanner({
          kind: 'error',
          message:
            'Your browser blocked the attachment tab — allow pop-ups for this site and try again.',
        });
        return;
      }
      popup.location.href = url;
    } catch (err) {
      popup?.close();
      setBanner({
        kind: 'error',
        message: err instanceof Error ? err.message : "Couldn't open this attachment — try again.",
      });
    } finally {
      setOpeningAttachmentId(null);
    }
  }

  // Re-seed local edit state only when the *submission itself* changes
  // (navigating to a different id) — not on every reference change of
  // `data`. React Query's `refetchOnWindowFocus` (throttled by a 30s
  // `staleTime`) and the 409-conflict retry path both call `refetch()`,
  // which resolves with a new `data` object for the SAME id. Keying this
  // effect on `data` would silently clobber in-progress edits on a
  // background refetch, and — worse — would clear the conflict banner the
  // instant the retry-refetch resolves, before the user can read it.
  const dataId = data?.id;
  useEffect(() => {
    if (data) {
      setStatus(data.status);
      setNotes(data.adminNotes ?? '');
      setBaseline(toBaseline(data));
      setPublicNote('');
      setBanner(null);
      // An erase confirmation (and its error) belongs to the submission it was opened on.
      setEraseOpen(false);
      setEraseError(null);
    }
    // Deliberately keyed on `dataId` only, not `data`; see the comment above.
    // (`react-hooks/exhaustive-deps` is off repo-wide, so no disable directive
    // is needed here; adding one trips the unused-directive lint gate.)
  }, [dataId]);

  // A refetch that lands while the form is UNTOUCHED (nothing edited since load) is safe to
  // adopt silently -- there is no edit to lose or mis-version. A touched form keeps its old
  // baseline on purpose: its eventual Save then 409s instead of overwriting a newer row.
  const dataVersion = data?.rowVersion;
  useEffect(() => {
    if (!data || !baseline) return;
    const untouched = status === baseline.status && notes === baseline.notes;
    if (untouched && data.rowVersion !== baseline.rowVersion) {
      setStatus(data.status);
      setNotes(data.adminNotes ?? '');
      setBaseline(toBaseline(data));
    }
  }, [dataVersion]);

  if (isPending) {
    return <div className="text-body-sm text-muted animate-pulse">Loading…</div>;
  }
  if (isError || !data) {
    return <ErrorState onRetry={() => void refetch()} />;
  }

  const nextStatuses = legalNextStatuses(data.status as FeedbackStatus);
  const base = baseline ?? toBaseline(data);
  const dirty = status !== base.status || notes !== base.notes;
  // The note for the submitter exists only on the move INTO a finished status (the server refuses
  // it anywhere else, and no email goes out otherwise).
  const enteringTerminal = status !== base.status && isTerminalStatus(status);

  // A 409 on ANY write. Do NOT keep the stale local edit in the form: the next Save would re-send it
  // under the NEW rowVersion, pass the optimistic lock, and silently overwrite the other operator's
  // change. Re-seed from the fresh row and show the lost draft.
  const handleVersionConflict = (draft: ConflictDraft) => {
    setBanner({ kind: 'conflict', draft });
    void Promise.resolve(refetch())
      .then((res) => {
        // TanStack Query v5 does NOT reject a failed refetch: it resolves with
        // `isError: true` and the last CACHED (stale) row. Only a successful refetch is
        // fresh enough to re-seed from.
        const result = res as { data?: AdminFeedbackDto; isError?: boolean } | undefined;
        const fresh = result?.isError ? undefined : result?.data;
        if (fresh) {
          setStatus(fresh.status);
          setNotes(fresh.adminNotes ?? '');
          setBaseline(toBaseline(fresh));
          setPublicNote('');
        }
      })
      // Defensive only (v5 resolves rather than rejects). A failed refetch leaves the OLD
      // baseline in place, so another Save still sends the old version and 409s again --
      // it can never overwrite the newer row.
      .catch(() => {});
  };

  // Assignment is its own write: it carries the loaded rowVersion (so it 409s on a stale view like
  // any other) but must never touch the status/notes the operator is still editing. A success
  // proves nothing else changed since load (the write is version-guarded), so only the version
  // baseline advances and the unsaved edits stay exactly as typed.
  const assign = (who: 'me' | 'none') => {
    setBanner(null);
    mutation.mutate(
      { assignee: who, expectedVersion: base.rowVersion },
      {
        onSuccess: (row) => {
          if (!row || typeof row.rowVersion !== 'number' || row.id !== id) return;
          setBaseline({ ...base, rowVersion: row.rowVersion });
        },
        onError: (err: unknown) => {
          if (isVersionConflict(err)) {
            handleVersionConflict({
              status: status !== base.status ? status : null,
              notes: notes !== base.notes ? notes : null,
              publicNote: enteringTerminal && publicNote.trim() ? publicNote.trim() : null,
            });
            return;
          }
          if (isRetryable(err)) {
            setBanner({ kind: 'retryable' });
            return;
          }
          setBanner({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Failed to update the assignee.',
          });
        },
      },
    );
  };

  // Erase on a privacy request (#14561). The operator typed the submission id to confirm and supplied the
  // privacy-request reference; the reference is checked here so a name or email never reaches the audit log.
  const erase = (rawRef: string) => {
    setBanner(null);
    if (!REQUEST_REF_PATTERN.test(rawRef)) {
      setEraseError(REQUEST_REF_HELP);
      return;
    }
    setEraseError(null);
    eraseMutation.mutate(rawRef, {
      onSuccess: () => {
        setEraseOpen(false);
        // Leave first, then drop the erased record's cache (see useEraseFeedbackSubmission).
        void Promise.resolve(navigate({ to: '/feedback' }))
          .then(() => forgetErasedSubmission(qc, id))
          // A failed navigation leaves this page mounted, so its cache stays (see above).
          .catch(() => {});
      },
      onError: (err: unknown) => {
        if (isEraseStorageIncomplete(err)) {
          setEraseError(ERASE_STORAGE_INCOMPLETE_TEXT);
          return;
        }
        // Gone already: most often an earlier attempt that ran but whose answer was lost.
        if (isApiError(err) && err.code === 'NOT_FOUND') {
          // onSuccess never ran for the earlier attempt, so refresh the inbox: a cached list must not still
          // show a request that is gone.
          void qc.invalidateQueries({ queryKey: ['feedback-submissions'] });
          setEraseError(ERASE_NOT_FOUND_TEXT);
          return;
        }
        if (isRetryable(err)) {
          setEraseError(ERASE_UNAVAILABLE_TEXT);
          return;
        }
        setEraseError(err instanceof Error ? err.message : 'Erasure failed. Try again.');
      },
    });
  };

  const save = () => {
    setBanner(null);
    const sentStatus = status;
    const sentNotes = notes;
    const sentPublicNote = enteringTerminal ? publicNote.trim() : '';
    const statusChanged = status !== base.status;
    const notesChanged = notes !== base.notes;
    // Send only what changed (an unchanged field is not a write), and a cleared notes box
    // as an explicit null rather than '' so the column goes back to NULL.
    const draft = {
      status: statusChanged ? status : null,
      notes: notesChanged ? notes : null,
      publicNote: sentPublicNote || null,
    };
    mutation.mutate(
      {
        ...(statusChanged ? { status } : {}),
        ...(notesChanged ? { adminNotes: notes.trim() === '' ? null : notes } : {}),
        ...(sentPublicNote ? { publicNote: sentPublicNote } : {}),
        expectedVersion: base.rowVersion,
      },
      {
        onSuccess: (row) => {
          // Our own write landed: the response is the new baseline. Ignore a response for a
          // submission we have since navigated away from (this component instance is reused).
          if (!row || typeof row.rowVersion !== 'number' || row.id !== id) return;
          setBaseline(toBaseline(row));
          // Only adopt a field if the operator hasn't typed on top of what was sent while the
          // save was in flight -- otherwise their newer text would silently vanish. Anything
          // they typed stays in the form and simply reads as dirty against the new baseline.
          setStatus((cur) => (cur === sentStatus ? row.status : cur));
          setNotes((cur) => (cur === sentNotes ? (row.adminNotes ?? '') : cur));
          if (sentPublicNote) setPublicNote((cur) => (cur.trim() === sentPublicNote ? '' : cur));
        },
        onError: (err: unknown) => {
          if (isVersionConflict(err)) {
            handleVersionConflict(draft);
            return;
          }
          if (isIllegalTransition(err)) {
            const body = err.body as { from?: string; to?: string } | undefined;
            setBanner({ kind: 'illegal', from: body?.from, to: body?.to });
            return;
          }
          if (isRetryable(err)) {
            setBanner({ kind: 'retryable' });
            return;
          }
          setBanner({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Failed to save changes.',
          });
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- plain back-nav button, no design variant needed */}
      <button
        type="button"
        onClick={() => void navigate({ to: '/feedback' })}
        className="inline-flex items-center gap-1 text-body-sm text-muted hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-0 self-start"
      >
        <ChevronLeft size={14} />
        Back to inbox
      </button>

      {banner?.kind === 'conflict' && (
        <div
          role="alert"
          className="rounded-control border border-warning bg-warning-pale text-warning-text text-body-sm p-4"
        >
          This submission changed since you loaded it. We&apos;ve reloaded the latest values —
          review and re-apply your change if it still applies.
          {banner.draft &&
            (banner.draft.status !== null ||
              banner.draft.notes !== null ||
              Boolean(banner.draft.publicNote)) && (
              <div className="mt-2 text-caption" data-testid="conflict-draft">
                Your unsaved edit:
                {banner.draft.status !== null && <> status → {statusLabel(banner.draft.status)}</>}
                {banner.draft.notes !== null && (
                  <> notes → &ldquo;{banner.draft.notes || '(cleared)'}&rdquo;</>
                )}
                {banner.draft.publicNote && (
                  <> note to the submitter → &ldquo;{banner.draft.publicNote}&rdquo;</>
                )}
              </div>
            )}
        </div>
      )}
      {banner?.kind === 'illegal' && (
        <div
          role="alert"
          className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-4"
        >
          Can&apos;t move this submission from {banner.from ?? data.status} to {banner.to ?? status}{' '}
          — that transition isn&apos;t allowed.
        </div>
      )}
      {banner?.kind === 'retryable' && (
        <div
          role="alert"
          className="rounded-control border border-warning bg-warning-pale text-warning-text text-body-sm p-4"
        >
          Feedback service is temporarily unavailable — try again.
        </div>
      )}
      {banner?.kind === 'error' && (
        <div
          role="alert"
          className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-4"
        >
          {banner.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-heading-2 text-primary">{data.title}</h1>
          <Badge variant={statusBadgeVariant(data.status)}>{statusLabel(data.status)}</Badge>
          <Badge variant={requestTypeVariant(data.requestType)}>{data.requestType}</Badge>
          <Badge variant={priorityVariant(data.priority)}>{data.priority}</Badge>
        </div>
        <p className="flex flex-wrap items-center gap-x-1 text-caption text-muted">
          {new Date(data.createdAt).toLocaleString()} · tenant
          <Button
            type="button"
            variant="link"
            className="text-caption"
            title={data.tenantId}
            onClick={() =>
              void navigate({ to: '/tenants/$tenantId', params: { tenantId: data.tenantId } })
            }
          >
            {data.tenantName ?? `${data.tenantId.slice(0, 8)}…`}
          </Button>
        </p>
      </div>

      {/* Read-only fields */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-control border border-border bg-surface p-4">
        <Field
          label="Submitted by"
          value={
            data.submittedByName || data.submittedByEmail
              ? `${data.submittedByName ?? 'Unknown'}${data.submittedByEmail ? ` (${data.submittedByEmail})` : ''}`
              : data.submittedBy
          }
        />
        <Field
          label="Module"
          value={data.submodule ? `${data.module} / ${data.submodule}` : data.module}
        />
        <Field label="Votes" value={data.voteCount} />
        <Field
          label="Tags"
          value={
            data.tags.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {data.tags.map((tag) => (
                  <Badge key={tag} variant="neutral">
                    {tag}
                  </Badge>
                ))}
              </div>
            ) : null
          }
        />
        {data.resolvedAt && (
          <Field
            label="Resolved"
            value={`${new Date(data.resolvedAt).toLocaleString()}${data.resolvedByName ? ` by ${data.resolvedByName}` : ''}`}
          />
        )}
        {data.publicNote && (
          <div className="sm:col-span-2">
            <Field
              label="Note sent to the submitter"
              value={<p className="whitespace-pre-wrap">{data.publicNote}</p>}
            />
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <Field
          label="Description"
          value={<p className="whitespace-pre-wrap">{data.description}</p>}
        />
        {data.businessImpact && (
          <Field
            label="Business impact"
            value={<p className="whitespace-pre-wrap">{data.businessImpact}</p>}
          />
        )}
        {data.currentWorkaround && (
          <Field
            label="Current workaround"
            value={<p className="whitespace-pre-wrap">{data.currentWorkaround}</p>}
          />
        )}
        {data.additionalNotes && (
          <Field
            label="Additional notes"
            value={<p className="whitespace-pre-wrap">{data.additionalNotes}</p>}
          />
        )}
      </section>

      <ContextSummary context={data.context ?? {}} />

      {Object.keys(data.context ?? {}).length > 0 && (
        <details className="rounded-control border border-border bg-surface p-3">
          <summary className="text-caption text-muted cursor-pointer">
            Context ({Object.keys(data.context).length} field
            {Object.keys(data.context).length === 1 ? '' : 's'})
          </summary>
          <pre className="mt-2 text-caption text-secondary whitespace-pre-wrap break-words">
            {JSON.stringify(data.context, null, 2)}
          </pre>
        </details>
      )}

      {(attachments.data?.length ?? 0) > 0 && (
        <section className="flex flex-col gap-2">
          <p className="text-caption text-muted">Attachments</p>
          <ul className="flex flex-wrap gap-3">
            {attachments.data!.map((a) => (
              <li key={a.id} className="flex flex-col items-center gap-1">
                {isAttachmentExpired(a.createdAt) ? (
                  // Past the retention window the bucket has deleted the bytes and the server answers 410, so
                  // offer no button. Checked before the scan state: an old pending row is gone too.
                  <span className="rounded-control border border-border bg-surface-sunken px-3 py-2 text-body-sm text-muted">
                    {a.fileName} — Expired ({ATTACHMENT_RETENTION_DAYS}-day retention)
                  </span>
                ) : a.avScanStatus === 'clean' ? (
                  // eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- attachment thumbnail button, no design variant needed
                  <button
                    type="button"
                    onClick={() => void openAttachment(a.id)}
                    disabled={openingAttachmentId === a.id}
                    aria-label={`Open ${a.fileName} (opens in new tab)`}
                    className="rounded-control border border-border bg-surface px-3 py-2 text-body-sm text-primary hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {openingAttachmentId === a.id ? 'Opening…' : a.fileName}
                  </button>
                ) : (
                  <span className="rounded-control border border-border bg-surface-sunken px-3 py-2 text-body-sm text-muted">
                    {a.fileName} — {a.avScanStatus === 'pending' ? 'Scanning…' : 'Unavailable'}
                  </span>
                )}
                <span className="text-caption text-muted">
                  {a.source === 'screen_capture' ? 'Captured' : 'Uploaded'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Triage editor */}
      <section className="flex flex-col gap-3 rounded-control border border-border bg-surface-sunken p-4">
        <h2 className="text-heading-3 text-primary">Triage</h2>
        {!canResolve && (
          <p className="text-caption text-muted">
            You have read-only access to this submission. Changing status or notes requires the
            feedback-resolve permission.
          </p>
        )}
        <div className="flex items-center gap-2 flex-wrap" data-testid="assignee-row">
          <span className="text-caption text-muted">Assigned to</span>
          <span className="text-body-sm text-primary">{data.assigneeName ?? 'Unassigned'}</span>
          {canResolve && (
            <>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={mutation.isPending}
                onClick={() => assign('me')}
              >
                Assign to me
              </Button>
              {data.assigneeUid && (
                <Button
                  type="button"
                  size="sm"
                  variant="link"
                  disabled={mutation.isPending}
                  onClick={() => assign('none')}
                >
                  Unassign
                </Button>
              )}
            </>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="feedback-status" className="text-caption text-muted">
            Status
          </label>
          <Select
            value={status}
            onValueChange={(v) => {
              setStatus(v);
              // A note typed for Completed/Declined must not survive a switch to an open status: it
              // would reappear prefilled on a later, different transition and go to the customer.
              if (!isTerminalStatus(v)) setPublicNote('');
            }}
          >
            <SelectTrigger
              id="feedback-status"
              disabled={!canResolve}
              className="h-[1.875rem] max-w-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={data.status}>{statusLabel(data.status)} (no change)</SelectItem>
              {nextStatuses.map((s) => (
                <SelectItem key={s} value={s}>
                  {statusLabel(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {enteringTerminal && canResolve && (
          <div className="flex flex-col gap-1">
            <label htmlFor="feedback-public-note" className="text-caption text-muted">
              Note to the submitter (optional)
            </label>
            <Textarea
              id="feedback-public-note"
              value={publicNote}
              onChange={(e) => setPublicNote(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Shown to the person who sent this on their My requests list (not included in the status email). Plain text."
            />
            <p className="text-caption text-muted">
              {publicNote.length}/500 · This is NOT private: use Internal notes for anything
              staff-only.
            </p>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label htmlFor="feedback-admin-notes" className="text-caption text-muted">
            Internal notes
          </label>
          <Textarea
            id="feedback-admin-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={4000}
            rows={4}
            disabled={!canResolve}
            placeholder="Add internal triage notes…"
          />
        </div>
        <div>
          <Button
            variant="primary"
            onClick={save}
            disabled={!canResolve || !dirty || mutation.isPending}
          >
            {mutation.isPending && mutation.variables?.assignee === undefined ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </section>

      {canErase && (
        <section
          aria-label="Privacy request"
          className="flex flex-col gap-3 rounded-control border border-border bg-surface p-4"
        >
          <h2 className="text-heading-3 text-primary">Privacy request</h2>
          <p className="text-body-sm text-secondary">
            Permanently erase this request and its screenshots when the submitter, or someone shown
            in it, has made a verified deletion request. This cannot be undone. Check the
            privacy-request runbook first, including whether a legal hold applies.
          </p>
          {!eraseOpen && (
            <div>
              <Button
                variant="danger"
                onClick={() => {
                  setEraseError(null);
                  setEraseOpen(true);
                }}
              >
                Erase this request
              </Button>
            </div>
          )}
          <TypedConfirm
            open={eraseOpen}
            title="Erase this request permanently?"
            body="This deletes the request, its notes and its screenshots, and records the erasure in the audit log with the reference below, never with any text from the request."
            error={eraseError ?? undefined}
            lockCancelWhilePending
            confirmValue={data.id}
            confirmLabel="Erase permanently"
            requireReason
            reasonLabel="Privacy-request reference (recorded in the audit log)"
            pending={eraseMutation.isPending}
            onCancel={() => {
              setEraseOpen(false);
              setEraseError(null);
            }}
            onConfirm={erase}
          />
        </section>
      )}
    </div>
  );
}
